import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import RegulatoryBadges from "@/components/RegulatoryBadges";

export default function Packages() {
  const { t, lang, pick } = useI18n();
  const sections = useSections();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/packages").then((r) => setItems(
    (r.data || []).filter((p) => p.is_active !== false && p.is_public !== false && p.is_archived !== true)
  )); }, []);

  if (!isSectionOn(sections, "packages")) return (
    <div className="container-x py-24 text-navy/60" data-testid="packages-off">
      {lang === "ar" ? "هذا القسم غير متاح حالياً." : "This section is currently unavailable."}
    </div>
  );

  return (
    <div data-testid="packages-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{t("nav.packages")}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {lang === "ar" ? "باقات مصمّمة لكل مرحلة" : "Packages Engineered for Every Stage"}
          </h1>
          <span className="divider-gold" />
        </div>
      </section>

      <section className="container-x py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((p) => {
          const soon = p.status === "coming_soon";
          return (
          <div
            key={p.id}
            data-testid={`pkg-${p.id}`}
            className={`p-8 border relative ${p.featured ? "bg-navy text-cream border-gold" : "bg-white text-navy border-navy/10"} ${soon ? "opacity-85" : ""}`}
          >
            {p.featured && !soon && (
              <span className="absolute -top-3 start-6 chip !bg-gold !text-navy !border-gold">
                <Star size={12} /> {lang === "ar" ? "الأكثر طلباً" : "Most popular"}
              </span>
            )}
            {soon && (
              <span className="absolute -top-3 start-6 chip !bg-amber-400 !text-navy !border-amber-500" data-testid={`pkg-soon-${p.id}`}>
                {lang === "ar" ? "قريباً" : "Coming soon"}
              </span>
            )}
            <h3 className={`text-2xl ${lang === "ar" ? "font-arabic-serif" : "font-serif"}`}>{pick(p, "name")}</h3>
            <RegulatoryBadges value={p.regulatory_frameworks} lang={lang} testId={`pkg-reg-${p.id}`} />
            <div className={`mt-4 text-3xl font-serif ${p.featured ? "text-gold" : "text-navy"}`}>
              {lang === "en" ? (p.price_en || p.price) : p.price}
            </div>
            <span className="divider-gold" />
            <ul className="space-y-3 text-sm">
              {(lang === "ar" ? p.features_ar : p.features_en).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={16} className="text-gold flex-shrink-0 mt-1" />
                  <span className={p.featured ? "text-cream/90" : "text-navy/80"}>{f}</span>
                </li>
              ))}
            </ul>
            {soon ? (
              <div className="mt-8 w-full text-center px-5 py-3 rounded-sm font-semibold border border-navy/20 text-navy/60 cursor-not-allowed" data-testid={`pkg-soon-cta-${p.id}`}>
                {lang === "ar" ? "قريباً" : "Coming soon"}
              </div>
            ) : (p.cta_show === false ? null : (() => {
              const action = p.cta_action || "quote";
              const label = (lang === "ar" ? p.cta_text_ar : p.cta_text_en)
                || (action === "contact"
                     ? (lang === "ar" ? "اتصل بنا" : "Contact us")
                     : action === "external"
                       ? (lang === "ar" ? "تعرف على المزيد" : "Learn more")
                       : t("nav.quote"));
              const cls = `mt-8 inline-block w-full text-center px-5 py-3 rounded-sm font-semibold transition-colors ${
                p.featured ? "bg-gold text-navy hover:brightness-110" : "border border-navy text-navy hover:bg-navy hover:text-cream"
              }`;
              if (action === "external" && p.cta_url) {
                return <a href={p.cta_url} target="_blank" rel="noopener noreferrer" data-testid={`pkg-select-${p.id}`} className={cls}>{label}</a>;
              }
              if (action === "contact") {
                return <Link to="/contact" data-testid={`pkg-select-${p.id}`} className={cls}>{label}</Link>;
              }
              return <Link to={`/quote?package=${encodeURIComponent(p.name_en || p.id)}`} data-testid={`pkg-select-${p.id}`} className={cls}>{label}</Link>;
            })())}
          </div>
        );})}
      </section>
    </div>
  );
}
